/**
 * @fileoverview Org portal — the Dashboard, the landing page (O9 reporting).
 *
 * What a director takes to a board, and what they check between meetings:
 * how much was taught and where, what is coming, who is teaching, who is on
 * the rosters, and what is waiting on someone. Laid out like the program and
 * class pages it reports on: a wide shell, the vital signs as compact tiles
 * under the title, and a URL-backed tab bar (F6) where each tile opens the tab
 * holding the rows behind its number.
 *
 *  - Overview: the chart over the chosen range beside what is on today
 *    (TodayCard), then where teaching happens, delivery and what needs
 *    attention.
 *  - Programs / Instructors: sortable tables, a row per program or person,
 *    each opening its own page.
 *  - Students: everyone on the org's rosters (OrgStudentsTab).
 *  - Lessons: every session across every program (OrgLessonsTab).
 *
 * **The range.** The Overview's range (past 30 days … all time, or two dates)
 * lives in the URL beside the tab and drives the Instructors teaching tile and
 * the Overview; the Programs and Instructors tables keep their labelled 30-day
 * and 12-month columns. Every number comes from `GET /org/analytics`, counted
 * by the same taught rule as the program pages, so a program's row here and
 * its page agree. The page itself only formats (`lib/orgReporting`).
 *
 * **What is coming.** The Upcoming tile counts the next 30 days from today,
 * whatever the range, so it is its own read (`GET /org/upcoming`), made once
 * beside the dashboard rather than with every range, and counted from the
 * Lessons tab's own rows so the tile opens the tab on exactly what it counted.
 * Sessions taught are the Overview chart's to show: its Total carries the
 * change against the range before it.
 *
 * **What is on today** is the Calendar's own read (`GET /org/calendar`) for
 * the organization's today, made once the dashboard knows which day that is
 * (`as_of`), so a meeting reads the same here as on the Calendar.
 *
 * **One Export, under the tabs.** It downloads whatever the open tab shows,
 * as CSV, so there is never a menu to read. An organisation that can get its
 * own data out is one that chose to stay rather than one that could not leave.
 *
 * Every organization has all of it: reporting and export used to be part of
 * the Brand & Business add-on, and became the landing page for everyone when
 * Reporting replaced the old dashboard (Sept 2026). The old dashboard's week
 * ahead was retired with it — the Calendar opens on this week — and its
 * Scheduler connection lives in Settings → Schedule (OrgSchedulePage).
 */

import { startTransition, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { cn } from "@/lib/utils";
import { useTabParam } from "@/lib/tabParam";
import { CSV_TYPE, ExportButton, exportSlug } from "@/components/studio/ExportMenu";
import TeachingVolumeChart, { SERIES } from "@/components/org/TeachingVolumeChart";
import TodayCard from "@/components/org/TodayCard";
import DashboardRangePicker from "@/components/org/DashboardRangePicker";
import OrgLessonsTab from "@/components/org/OrgLessonsTab";
import { chaseOutcome } from "@/lib/orgSessions";
import OrgStudentsTab from "@/components/org/OrgStudentsTab";
import ReportTable from "@/components/org/ReportTable";
import {
  Avatar,
  Button,
  Card,
  Icon,
  PageShell,
  SegmentedControl,
  StatCard,
  Tag,
  TextTabs,
} from "@/components/studio/primitives";
import {
  DEFAULT_RANGE,
  NOTES_WINDOW_DAYS,
  PROGRAM_TYPE_LABELS,
  RANGES,
  attentionItems,
  change,
  changeLabel,
  comparisonLabel,
  dayLabel,
  formatCount,
  percent,
  rangeQuery,
  readRange,
  seriesBuckets,
} from "@/lib/orgReporting";

const TABS = [
  { id: "overview", label: "Overview", testId: "dashboard-tab-overview" },
  { id: "programs", label: "Programs", testId: "dashboard-tab-programs" },
  { id: "instructors", label: "Instructors", testId: "dashboard-tab-instructors" },
  { id: "students", label: "Students", testId: "dashboard-tab-students" },
  { id: "lessons", label: "Lessons", testId: "dashboard-tab-lessons" },
];

/**
 * What the Export under the tab bar downloads on each tab. `ranged` exports
 * follow the dashboard's range, and their file says which range it was.
 */
const TAB_EXPORTS = {
  overview: { kind: "overview", what: "overview", ranged: true },
  programs: { kind: "programs", what: "programs", ranged: true },
  instructors: { kind: "instructors", what: "instructors", ranged: true },
  students: { kind: "students", what: "students" },
  lessons: { kind: "lessons", what: "lessons" },
};

function exportItem(tab, range, rangeLabel) {
  const spec = TAB_EXPORTS[tab] || TAB_EXPORTS.overview;
  const suffix = spec.ranged ? `-${exportSlug(rangeLabel, "range")}` : "";
  return {
    url: `/org/export/${spec.kind}${spec.ranged ? `?${rangeQuery(range)}` : ""}`,
    filename: `hans-${spec.kind}${suffix}.csv`,
    type: CSV_TYPE,
    label: `Export ${spec.what}`,
    what: spec.what,
  };
}

/* ------------------------------------------------------------------ */
/*  Small pieces                                                        */
/* ------------------------------------------------------------------ */

/**
 * The change between the last 30 days and the 30 before, in words and one
 * glyph. Up reads as good here because every figure it sits beside is
 * teaching done; the direction is always also in the text, never colour alone.
 */
function Delta({ current, previous, suffix = "vs prior 30 days", countFromZero = false, className, testId }) {
  const c = change(current, previous);
  // From nothing there is no percentage; a week's count reads better than "New".
  const label = countFromZero && c.pct === null ? `+${formatCount(c.diff)}` : changeLabel(c);
  const tone =
    c.direction === "up"
      ? "text-[var(--state-success)]"
      : c.direction === "down"
        ? "text-[var(--state-danger)]"
        : "text-[var(--text-tertiary)]";
  const icon = c.direction === "up" ? "trendUp" : c.direction === "down" ? "trendDown" : "minus";
  return (
    <span className={cn("inline-flex flex-wrap items-center gap-x-1", className)} data-testid={testId}>
      <span className={cn("inline-flex items-center gap-0.5 font-medium", tone)}>
        <Icon name={icon} size={13} strokeWidth={2} />
        {label}
      </span>
      {suffix && <span className="text-[var(--text-tertiary)]">{suffix}</span>}
    </span>
  );
}

/**
 * The Upcoming tile's number and the line under it. Booked in Kalver
 * Scheduler, private lessons are not Hans's to count, so an org running only
 * those reads "—", the way such a program's own tile does; with some sessions
 * there, the line says the figure is Hans's classes only.
 */
function upcomingTile(upcoming) {
  if (!upcoming) {
    return {
      value: (
        <>
          <span aria-hidden className="skeleton block h-[26px] w-12 rounded-md" />
          <span className="sr-only">Loading</span>
        </>
      ),
      sub: "Next 30 days",
    };
  }
  if (upcoming.failed) return { value: "—", sub: "Couldn't load" };
  const label = upcoming.window?.label || "Next 30 days";
  if (upcoming.private_lessons_only) return { value: "—", sub: "Booked in Scheduler" };
  return { value: formatCount(upcoming.count), sub: upcoming.complete ? label : `${label} · Hans classes only` };
}

function CardTitle({ title, sub, action, onAction, actionTestId }) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="font-sans text-lg font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{title}</h2>
        {sub && <p className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">{sub}</p>}
      </div>
      {action && (
        <button
          type="button"
          onClick={onAction}
          data-testid={actionTestId}
          className="inline-flex shrink-0 items-center gap-1 font-sans text-[14px] font-medium text-[var(--accent-yellow-text)] hover:underline"
        >
          {action} <Icon name="chevronRight" size={15} />
        </button>
      )}
    </div>
  );
}

/** A proportion as a bar: the fill on a lighter step of the same hue. */
function Meter({ value, testId }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--accent-yellow-dim)]" data-testid={testId} aria-hidden>
      <div className="h-full rounded-full bg-[var(--accent-yellow)]" style={{ width: `${pct}%` }} />
    </div>
  );
}

/**
 * Parts of a whole, side by side with a 2px surface gap. Colour carries
 * identity only alongside a legend that names each part.
 */
function SplitBar({ parts, height = 8, testId }) {
  const total = parts.reduce((sum, p) => sum + (p.value || 0), 0);
  if (!total) {
    return <div className="w-full rounded-full bg-[var(--studio-interactive)]" style={{ height }} data-testid={testId} aria-hidden />;
  }
  return (
    <div className="flex w-full gap-[2px] overflow-hidden rounded-full" style={{ height }} data-testid={testId} aria-hidden>
      {parts
        .filter((p) => p.value > 0)
        .map((p) => (
          <div key={p.id} className="h-full" style={{ width: `${(p.value / total) * 100}%`, background: p.color }} />
        ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tables                                                              */
/* ------------------------------------------------------------------ */

function NotesOwedCell({ program }) {
  if (program.notes_owed === null || program.notes_owed === undefined) {
    return (
      <span className="text-[var(--text-tertiary)]" title="Private lessons have no class meetings to write up">
        —
      </span>
    );
  }
  const incomplete = program.notes_complete === false;
  return (
    <span className="inline-flex items-center justify-end gap-1">
      {program.notes_owed > 0 ? <Tag accent>{program.notes_owed}</Tag> : <span className="text-[var(--text-tertiary)]">0</span>}
      {incomplete && (
        <span title="Some of this program's classes are scheduled in Kalver Scheduler and can't be checked">
          <Icon name="info" size={14} className="text-[var(--text-tertiary)]" />
        </span>
      )}
    </span>
  );
}

/** A Programs cell's place while GET /org/upcoming loads, or its dash if it failed. */
function PendingCell({ loading }) {
  return loading ? (
    <span aria-hidden className="skeleton ml-auto block h-4 w-8 rounded" />
  ) : (
    <span className="text-[var(--text-tertiary)]">—</span>
  );
}

/**
 * A program's Sessions taught: every session held since it began, class
 * meetings written up or not, with how many were written up under the number
 * when that is not all of them (the Missing notes column says which recently).
 */
function HeldCell({ row, loading }) {
  if (!row) return <PendingCell loading={loading} />;
  return (
    <span className="inline-flex flex-col items-end">
      {formatCount(row.held)}
      {row.held > 0 && row.written < row.held && (
        <span className="whitespace-nowrap font-sans text-[11px] text-[var(--text-tertiary)]">
          {formatCount(row.written)} written up
        </span>
      )}
    </span>
  );
}

/**
 * A program's Remaining: its sessions still to come until it ends, its end
 * date or, without one, its classes' last scheduled meeting, said under the
 * number. A class that repeats with no end leaves no rest to count, so the
 * next 30 days stand in, said so. Private lessons are booked in Kalver
 * Scheduler, so they read "—", and a program with Scheduler classes says the
 * count is its Hans classes only.
 */
function RemainingCell({ row, loading }) {
  if (!row) return <PendingCell loading={loading} />;
  if (row.private_lessons) {
    return (
      <span className="text-[var(--text-tertiary)]" title="Private lessons are booked in Kalver Scheduler">
        —
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col items-end">
      <span className="inline-flex items-center gap-1">
        {formatCount(row.remaining)}
        {!row.complete && (
          <span title="Some of this program's classes are scheduled in Kalver Scheduler and can't be counted">
            <Icon name="info" size={14} className="text-[var(--text-tertiary)]" />
          </span>
        )}
      </span>
      <span
        className="whitespace-nowrap font-sans text-[11px] text-[var(--text-tertiary)]"
        title={row.until ? undefined : "A class repeats with no end date, so the next 30 days are counted"}
      >
        {row.until ? `to ${dayLabel(row.until, { year: true })}` : "next 30 days"}
      </span>
    </span>
  );
}

function programColumns(navigate, schedule) {
  const scheduleOf = (p) => schedule.rows.get(p.program_id);
  return [
    {
      id: "title",
      label: "Program",
      sortValue: (p) => (p.title || "").toLowerCase(),
      render: (p) => (
        <div className="min-w-[160px]">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/org/programs/${p.program_id}`);
            }}
            className="text-left font-sans text-[15px] font-medium text-[var(--text-primary)] hover:underline"
          >
            {p.title}
          </button>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 font-sans text-[12px] text-[var(--text-tertiary)]">
            <span>
              {[
                PROGRAM_TYPE_LABELS[p.program_type] || null,
                p.classes ? `${p.classes} class${p.classes === 1 ? "" : "es"}` : null,
                p.last_taught ? `Last taught ${dayLabel(p.last_taught)}` : "Nothing taught yet",
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
            {p.lifecycle === "past" && <Tag className="px-2 py-0.5 text-[11px]">Past</Tag>}
            {p.unassigned_classes > 0 && (
              <Tag accent className="px-2 py-0.5 text-[11px]">
                {p.unassigned_classes} unassigned
              </Tag>
            )}
            {p.pending_applications > 0 && (
              <Tag accent className="px-2 py-0.5 text-[11px]">
                {p.pending_applications} to review
              </Tag>
            )}
            {/* On a phone the Missing notes column is dropped; its count
                rides here instead, so it is never lost. */}
            {p.notes_owed > 0 && (
              <Tag accent className="px-2 py-0.5 text-[11px] sm:hidden">
                {p.notes_owed} missing notes
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "held",
      label: "Sessions taught",
      sortValue: (p) => scheduleOf(p)?.held ?? -1,
      render: (p) => <HeldCell row={scheduleOf(p)} loading={schedule.loading} />,
    },
    {
      id: "change",
      label: "Change",
      className: "hidden md:table-cell",
      sortValue: (p) => {
        const row = scheduleOf(p);
        return row ? row.held_week - row.held_prev_week : -Infinity;
      },
      render: (p) => {
        const row = scheduleOf(p);
        if (!row) return <PendingCell loading={schedule.loading} />;
        return (
          <span className="font-sans text-[13px]">
            <Delta
              current={row.held_week}
              previous={row.held_prev_week}
              suffix="vs last week"
              countFromZero
              className="flex-nowrap whitespace-nowrap"
            />
          </span>
        );
      },
    },
    {
      id: "remaining",
      label: "Remaining",
      className: "hidden sm:table-cell",
      sortValue: (p) => {
        const row = scheduleOf(p);
        return row && !row.private_lessons ? row.remaining : -1;
      },
      render: (p) => <RemainingCell row={scheduleOf(p)} loading={schedule.loading} />,
    },
    {
      id: "students_reached",
      label: "Students",
      className: "hidden md:table-cell",
      sortValue: (p) => p.students_reached || 0,
      render: (p) => formatCount(p.students_reached || 0),
    },
    {
      id: "instructors",
      label: "Instructors",
      className: "hidden lg:table-cell",
      sortValue: (p) => p.instructors || 0,
      render: (p) => formatCount(p.instructors || 0),
    },
    {
      id: "notes_owed",
      label: "Missing notes",
      className: "hidden sm:table-cell",
      sortValue: (p) => p.notes_owed ?? -1,
      render: (p) => <NotesOwedCell program={p} />,
    },
  ];
}

function instructorColumns(navigate) {
  return [
    {
      id: "name",
      label: "Instructor",
      sortValue: (r) => (r.name || "").toLowerCase(),
      render: (r) => (
        <div className="flex min-w-[160px] items-center gap-3">
          <Avatar name={r.name} size={32} />
          <div className="min-w-0">
            {r.member ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/org/instructors/${r.user_id}`);
                }}
                className="text-left font-sans text-[15px] font-medium text-[var(--text-primary)] hover:underline"
              >
                {r.name}
              </button>
            ) : (
              <span className="font-sans text-[15px] font-medium text-[var(--text-primary)]">{r.name}</span>
            )}
            <div className="mt-0.5 flex flex-wrap items-center gap-2 font-sans text-[12px] text-[var(--text-tertiary)]">
              {!r.member && <Tag className="px-2 py-0.5 text-[11px]">No longer a member</Tag>}
              {r.member && !r.sessions_30d && (
                <Tag accent className="px-2 py-0.5 text-[11px]">
                  No sessions in 30 days
                </Tag>
              )}
              <span className="sm:hidden">{r.last_taught ? `Last taught ${dayLabel(r.last_taught)}` : null}</span>
            </div>
          </div>
        </div>
      ),
    },
    { id: "sessions_30d", label: "Last 30 days", sortValue: (r) => r.sessions_30d || 0, render: (r) => formatCount(r.sessions_30d || 0) },
    {
      id: "change",
      label: "Change",
      className: "hidden md:table-cell",
      sortValue: (r) => (r.sessions_30d || 0) - (r.sessions_prev_30d || 0),
      render: (r) => (
        <span className="font-sans text-[13px]">
          <Delta current={r.sessions_30d} previous={r.sessions_prev_30d} suffix={null} />
        </span>
      ),
    },
    {
      id: "split",
      label: "Class · Solo",
      className: "hidden md:table-cell",
      render: (r) => (
        <span className="text-[var(--text-secondary)]">
          {formatCount(r.class_sessions_30d || 0)} · {formatCount(r.solo_30d || 0)}
        </span>
      ),
    },
    { id: "sessions_12m", label: "12 months", className: "hidden sm:table-cell", sortValue: (r) => r.sessions_12m || 0, render: (r) => formatCount(r.sessions_12m || 0) },
    {
      id: "last_taught",
      label: "Last taught",
      className: "hidden sm:table-cell",
      sortValue: (r) => r.last_taught || "",
      render: (r) => <span className="font-sans text-[13px] text-[var(--text-secondary)]">{r.last_taught ? dayLabel(r.last_taught, { year: true }) : "—"}</span>,
    },
  ];
}

/* ------------------------------------------------------------------ */
/*  Overview cards                                                      */
/* ------------------------------------------------------------------ */

/* "Class meetings with no notes" carries Chase all (C4): every class that owes
   notes asks its instructors, in their updates, never by email. It is its own
   button beside the row, which still opens where the meetings are. */
function AttentionCard({ items, onOpen, onChaseAll, chasing = false }) {
  return (
    <Card padding={0} className="flex max-h-[440px] flex-col overflow-hidden" data-testid="dashboard-attention">
      <div className="flex shrink-0 items-center justify-between gap-3 px-5 pb-3 pt-5">
        <h2 className="font-sans text-lg font-semibold tracking-[-0.01em] text-[var(--text-primary)]">Needs attention</h2>
        {items.length > 0 && <Tag accent>{items.length}</Tag>}
      </div>
      {items.length === 0 ? (
        <div className="flex items-center gap-3 px-5 pb-5" data-testid="dashboard-attention-clear">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--state-success-border)] bg-[var(--state-success-dim)]">
            <Icon name="check" size={16} strokeWidth={2} className="text-[var(--state-success)]" />
          </span>
          <p className="font-sans text-[14px] text-[var(--text-secondary)]">Nothing is waiting on anyone right now.</p>
        </div>
      ) : (
        <ul className="min-h-0 flex-1 overflow-y-auto" data-testid="dashboard-attention-list">
          {items.map((item) => {
            const actionable = Boolean(item.tab || item.path);
            const body = (
              <>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)]">
                  <Icon name={item.icon} size={16} strokeWidth={1.8} className="text-[var(--accent-yellow-text)]" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-sans text-[14px] text-[var(--text-primary)]">
                    {item.count !== null && <span className="mr-1.5 font-mono font-medium">{formatCount(item.count)}</span>}
                    {item.title}
                  </span>
                  {item.detail && <span className="mt-0.5 block truncate font-sans text-[12px] text-[var(--text-tertiary)]">{item.detail}</span>}
                </span>
                {actionable && <Icon name="chevronRight" size={16} className="shrink-0 text-[var(--text-tertiary)]" />}
              </>
            );
            const chase = item.id === "notes" && onChaseAll;
            return (
              <li
                key={item.id}
                className={`border-t border-[var(--studio-divider)] ${chase ? "flex items-center" : ""}`}
                data-testid={`attention-${item.id}`}
              >
                {actionable ? (
                  <button
                    type="button"
                    onClick={() => onOpen(item)}
                    className={`flex items-center gap-3 px-5 py-3 text-left transition-colors duration-150 hover:bg-[var(--studio-overlay)] ${
                      chase ? "min-w-0 flex-1" : "w-full"
                    }`}
                  >
                    {body}
                  </button>
                ) : (
                  <div className="flex items-center gap-3 px-5 py-3">{body}</div>
                )}
                {chase && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={chasing}
                    onPress={onChaseAll}
                    className="mr-4 shrink-0"
                    aria-label={chasing ? "Asking for the missing notes" : "Chase all: ask the instructors for the missing notes"}
                    data-testid="attention-notes-chase"
                  >
                    {chasing ? "Asking…" : "Chase all"}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function SmsCard({ sms }) {
  const used = percent(sms.sent, sms.allowance);
  return (
    <Card padding={24} className="h-full">
      <CardTitle title="Text reminders" sub="This month's allowance" />
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="font-mono text-[22px] font-light text-[var(--text-primary)]">
          {formatCount(sms.sent)}
          <span className="text-[15px] text-[var(--text-tertiary)]"> / {formatCount(sms.allowance)}</span>
        </span>
        {used !== null && <span className="font-sans text-[13px] text-[var(--text-tertiary)]">{used}% used</span>}
      </div>
      <Meter value={used} testId="sms-meter" />
      <p className="mt-3 font-sans text-[13px] leading-relaxed text-[var(--text-secondary)]" data-testid="analytics-sms">
        {formatCount(sms.sent)} of {formatCount(sms.allowance)} sent this month — {sms.per_student} per active student.
        Reminders pause when the allowance is used up; we never bill you for extra.
      </p>
    </Card>
  );
}

/** A program row's sessions in the chosen range, split; the 12-month figures
    stand in for a response from before ranges existed. */
function rangeCounts(p) {
  if (p.period_sessions === undefined) return { sessions: p.lessons || 0, class_sessions: p.class_sessions || 0, solo: p.solo || 0 };
  return { sessions: p.period_sessions || 0, class_sessions: p.period_class_sessions || 0, solo: p.period_solo || 0 };
}

function ProgramsCard({ programs, rangeLabel, onOpen, onSeeAll }) {
  const shown = programs
    .map((p) => ({ ...p, range: rangeCounts(p) }))
    .filter((p) => p.range.sessions > 0)
    .sort((a, b) => b.range.sessions - a.range.sessions)
    .slice(0, 6);
  const top = Math.max(1, ...shown.map((p) => p.range.sessions));
  return (
    <Card padding={24} className="h-full" data-testid="dashboard-top-programs">
      <CardTitle
        title="Top programs"
        sub={`Sessions by program, ${rangeLabel.toLowerCase()}`}
        action="All programs"
        onAction={onSeeAll}
        actionTestId="dashboard-see-programs"
      />
      {shown.length === 0 ? (
        <p className="font-sans text-[14px] text-[var(--text-tertiary)]">No program taught a session in this range.</p>
      ) : (
        <>
          <ul className="space-y-4">
            {shown.map((p) => (
              <li key={p.program_id}>
                <button type="button" onClick={() => onOpen(p)} className="group block w-full text-left">
                  <div className="mb-1.5 flex items-baseline justify-between gap-3">
                    <span className="min-w-0 truncate font-sans text-[14px] font-medium text-[var(--text-primary)] group-hover:underline">
                      {p.title}
                    </span>
                    <span className="shrink-0 font-mono text-[14px] tabular-nums text-[var(--text-primary)]">
                      {formatCount(p.range.sessions)}
                    </span>
                  </div>
                  {/* Bar length is the program's share of the busiest one;
                      its split keeps the chart's class/solo colours. */}
                  <div style={{ width: `${Math.max(4, (p.range.sessions / top) * 100)}%` }}>
                    <SplitBar
                      height={6}
                      parts={SERIES.map((s) => ({ id: s.id, value: p.range[s.id] || 0, color: s.color }))}
                    />
                  </div>
                  <div className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
                    {formatCount(p.range.class_sessions)} class · {formatCount(p.range.solo)} solo
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

function DeliveryCard({ period, teaching, instructors }) {
  const sharedPct = percent(period.shared, period.sessions);
  const teachingPct = percent(teaching, instructors);
  const mix = SERIES.map((s) => ({ ...s, value: period[s.id] || 0 }));
  return (
    <Card padding={24} className="h-full" data-testid="dashboard-delivery">
      <CardTitle title="Delivery" sub={period.label || "Last 30 days"} />
      <div className="space-y-5">
        <div>
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <span className="font-sans text-[14px] text-[var(--text-primary)]">Notes sent to families</span>
            <span className="font-mono text-[15px] text-[var(--text-primary)]" data-testid="delivery-shared">
              {sharedPct === null ? "—" : `${sharedPct}%`}
            </span>
          </div>
          <Meter value={sharedPct} />
          <p className="mt-1.5 font-sans text-[12px] text-[var(--text-tertiary)]">
            {formatCount(period.shared)} of {formatCount(period.sessions)} sessions with notes
          </p>
        </div>
        <div>
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <span className="font-sans text-[14px] text-[var(--text-primary)]">Instructors teaching</span>
            <span className="font-mono text-[15px] text-[var(--text-primary)]" data-testid="delivery-teaching">
              {teachingPct === null ? "—" : `${teachingPct}%`}
            </span>
          </div>
          <Meter value={teachingPct} />
          <p className="mt-1.5 font-sans text-[12px] text-[var(--text-tertiary)]">
            {formatCount(teaching)} of {formatCount(instructors)} taught at least one session
          </p>
        </div>
        <div>
          <div className="mb-2 font-sans text-[14px] text-[var(--text-primary)]">Class sessions and solo lessons</div>
          <SplitBar parts={mix} testId="delivery-mix" />
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
            {mix.map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1.5 font-sans text-[12px] text-[var(--text-secondary)]">
                <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: s.color }} />
                {s.label}
                <span className="font-mono tabular-nums text-[var(--text-primary)]">{formatCount(s.value)}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  States                                                              */
/* ------------------------------------------------------------------ */

function LoadingState() {
  return (
    <PageShell width="wide" data-testid="dashboard-loading" aria-busy="true">
      <span className="sr-only">Loading the dashboard…</span>
      <div className="skeleton mb-2 h-8 w-40 rounded-lg" />
      <div className="skeleton mb-6 h-4 w-72 rounded-md" />
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="skeleton h-[88px] rounded-[20px]" />
        ))}
      </div>
      <div className="skeleton mb-6 h-11 w-[420px] max-w-full rounded-full" />
      <div className="grid gap-6 xl:grid-cols-3">
        <div className="skeleton h-[420px] rounded-[20px] xl:col-span-2" />
        <div className="skeleton h-[420px] rounded-[20px]" />
      </div>
    </PageShell>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

/** The dashboard's range, kept in the URL beside the tab (`?range=…`). */
function useRangeParam() {
  const [params, setParams] = useSearchParams();
  const range = readRange(params.get("range"), params.get("from"), params.get("to"));
  const setRange = useCallback(
    (next) => {
      setParams((prev) => {
        const out = new URLSearchParams(prev);
        if (!next?.id || next.id === DEFAULT_RANGE) out.delete("range");
        else out.set("range", next.id);
        if (next?.id === "custom") {
          out.set("from", next.start);
          out.set("to", next.end);
        } else {
          out.delete("from");
          out.delete("to");
        }
        return out;
      });
    },
    [setParams],
  );
  return { range, setRange };
}

export default function OrgDashboardPage() {
  const navigate = useNavigate();
  const { tab, filter, setTab } = useTabParam(TABS, "overview");
  const { range, setRange } = useRangeParam();
  const query = rangeQuery(range);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [upcoming, setUpcoming] = useState(null);
  const [chasingAll, setChasingAll] = useState(false);
  const [today, setToday] = useState(null);
  // Bumped when a tile opens the Lessons tab, so the tab opens afresh on the
  // rows behind the tile's number even when it is already the open one.
  const [lessonsVisit, setLessonsVisit] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const resp = await orgApi.get(`/org/analytics?${query}`);
      setData(resp.data);
    } catch (err) {
      setFailed(true);
      toast.error(getOrgErrorMessage(err, "Couldn't load the dashboard"));
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  // Read once: the next 30 days do not move with the range.
  useEffect(() => {
    let live = true;
    orgApi
      .get("/org/upcoming")
      .then((res) => live && setUpcoming(res.data))
      .catch(() => live && setUpcoming({ failed: true }));
    return () => {
      live = false;
    };
  }, []);

  // Today's meetings, for the organization's today: known once the dashboard
  // has answered, and read again only if the day itself changes.
  const asOf = data?.as_of || null;
  const [todayTry, setTodayTry] = useState(0);
  useEffect(() => {
    if (!asOf) return undefined;
    let live = true;
    setToday(null);
    orgApi
      .get(`/org/calendar?start=${asOf}&end=${asOf}`)
      .then((res) => live && setToday(res.data || {}))
      .catch(() => live && setToday({ failed: true }));
    return () => {
      live = false;
    };
  }, [asOf, todayTry]);

  const programs = useMemo(() => data?.by_program || [], [data]);
  const instructors = useMemo(() => data?.by_instructor || [], [data]);
  const attention = useMemo(() => attentionItems(data), [data]);
  const series = useMemo(() => seriesBuckets(data), [data]);
  // The Programs tab's sessions held, this week and remaining come with the
  // Upcoming tile's read (GET /org/upcoming), from the Lessons tab's own rows.
  const schedule = useMemo(
    () => ({ rows: new Map((upcoming?.by_program || []).map((r) => [r.program_id, r])), loading: !upcoming }),
    [upcoming],
  );
  const programCols = useMemo(() => programColumns(navigate, schedule), [navigate, schedule]);
  const instructorCols = useMemo(() => instructorColumns(navigate), [navigate]);

  if (loading && !data) return <LoadingState />;
  if (!data) {
    return (
      <PageShell width="wide">
        <h1 className="mb-6 font-serif text-2xl font-semibold text-[var(--text-primary)]">Dashboard</h1>
        <Card padding={24} className="max-w-3xl">
          <p className="font-sans text-[15px] text-[var(--text-secondary)]">
            {failed ? "The dashboard couldn't be loaded." : "Nothing to show yet."}
          </p>
          {failed && (
            <Button variant="ghost" icon="refresh" className="mt-4" onPress={load} data-testid="dashboard-retry">
              Try again
            </Button>
          )}
        </Card>
      </PageShell>
    );
  }

  const period = data.period || null;
  const rangeLabel = period?.label || RANGES.find((r) => r.id === range.id)?.label || "Past 30 days";
  const versus = comparisonLabel(period);
  const running = programs.filter((p) => p.lifecycle !== "past");
  const past = programs.filter((p) => p.lifecycle === "past");
  const runningClasses = running.reduce((sum, p) => sum + (p.classes || 0), 0);

  const programFilter = tab === "programs" && ["past", "all"].includes(filter) ? filter : "active";
  const shownPrograms = programFilter === "all" ? programs : programFilter === "past" ? past : running;
  const members = instructors.filter((i) => i.member);
  const idle = members.filter((i) => !i.sessions_30d);
  // Current members who taught in the range, so the figure can never exceed
  // the "of N" it is read against. Falls back to the window's count when the
  // rows are absent.
  const taughtInRange = (i) => (i.period_sessions !== undefined ? i.period_sessions : i.sessions_30d) > 0;
  const teaching = data.by_instructor ? members.filter(taughtInRange).length : period?.active_instructors;
  const instructorFilter = tab === "instructors" && filter === "idle" ? "idle" : "all";
  const shownInstructors = instructorFilter === "idle" ? idle : instructors;

  const openAttention = (item) => {
    if (item.path) navigate(item.path);
    else if (item.tab) setTab(item.tab, item.filter);
  };

  // Needs attention's Chase all (C4): every class that owes notes asks its
  // instructors, in their updates.
  const chaseAll = async () => {
    if (chasingAll) return;
    setChasingAll(true);
    try {
      const res = await orgApi.post("/org/notes-chase", { all: true });
      toast.success(chaseOutcome(res.data?.chased));
    } catch (err) {
      if (err?.response?.status === 409) toast(getOrgErrorMessage(err, "None of these classes owe notes."));
      else toast.error(getOrgErrorMessage(err, "Couldn't send the reminder"));
    } finally {
      setChasingAll(false);
    }
  };

  const coming = upcomingTile(upcoming);
  const openUpcoming = () => {
    setTab("lessons", "upcoming");
    // In a transition, as react-router moves the URL, so the tab mounts afresh
    // on the new filter rather than a render before it.
    startTransition(() => setLessonsVisit((n) => n + 1));
  };

  const exportButton = (
    <ExportButton client={orgApi} errorMessage={getOrgErrorMessage} item={exportItem(tab, range, rangeLabel)} testId="dashboard-export" />
  );

  const sum = (rows, key) => rows.reduce((s, r) => s + (r[key] || 0), 0);
  // A schedule figure added up over the programs shown.
  const scheduleSum = (key, keep = () => true) =>
    shownPrograms.reduce((s, p) => {
      const row = schedule.rows.get(p.program_id);
      return s + (row && keep(row) ? row[key] || 0 : 0);
    }, 0);

  return (
    <PageShell width="wide" data-testid="dashboard-page">
      {/* ---- Title and scope ------------------------------------------ */}
      <div className="mb-6 min-w-0">
        <h1 className="font-serif text-2xl font-semibold text-[var(--text-primary)]">Dashboard</h1>
        <p className="mt-1.5 max-w-3xl font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]">
          Teaching, reach and delivery across your programs. Only org-attributed lessons and students are counted.
        </p>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="dashboard-window">
          {period && (
            <span>
              {rangeLabel} · {dayLabel(period.start, { year: true })} – {dayLabel(period.end, { year: true })}
            </span>
          )}
          {data.as_of && <span>As of {dayLabel(data.as_of, { year: true })}</span>}
          {loading && <span aria-live="polite">Updating…</span>}
        </div>
      </div>

      {/* ---- Vital signs ---------------------------------------------- */}
      {/* Under the title and above the tab bar (decision D1); each opens the
          tab that holds the rows behind its number (F6). Upcoming looks 30
          days ahead from today whatever the range; instructors teaching
          follows the range; programs and students are the rosters as they
          stand today. Sessions taught are the Overview chart's. */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="dashboard-tiles">
        <StatCard
          compact
          value={coming.value}
          label="Upcoming"
          sub={coming.sub}
          onPress={openUpcoming}
          data-testid="dashboard-tile-upcoming"
        />
        <StatCard
          compact
          value={formatCount(running.length)}
          label="Active programs"
          sub={`${formatCount(runningClasses)} class${runningClasses === 1 ? "" : "es"} running`}
          onPress={() => setTab("programs")}
          data-testid="dashboard-tile-programs"
        />
        <StatCard
          compact
          value={formatCount(teaching ?? data.instructors)}
          label="Instructors teaching"
          sub={`of ${formatCount(data.instructors)} · ${rangeLabel.toLowerCase()}`}
          onPress={() => setTab("instructors")}
          data-testid="dashboard-tile-instructors"
        />
        <StatCard
          compact
          value={formatCount(data.students)}
          label="Students"
          sub="on active rosters"
          onPress={() => setTab("students")}
          data-testid="dashboard-tile-students"
        />
      </div>

      {/* The Overview's range sits beside the tabs, and every tab's Export at
          the row's end, so the tab's content starts straight beneath. Five
          tabs are wider than a phone: there the bar scrolls in its own row
          rather than widening the page, and the range and Export drop
          beneath it. */}
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center" data-testid="dashboard-tab-row">
        <div className="-mx-4 min-w-0 overflow-x-auto px-4 md:mx-0 md:px-0">
          <SegmentedControl testId="dashboard-tabs" value={tab} onChange={(id) => setTab(id)} options={TABS} />
        </div>
        {tab === "overview" && <DashboardRangePicker value={range} onChange={setRange} max={data.as_of} />}
        <div className="md:ml-auto">{exportButton}</div>
      </div>

      {/* ---- Under the tabs: the tab's own filters ---------------------- */}
      {/* Only where a tab has filters of its own here (Programs, Instructors):
          a row holding nothing but space would push the tab's content down. */}
      {(tab === "programs" || tab === "instructors") && (
        <div className="mb-5 flex flex-wrap items-center gap-3" data-testid="dashboard-toolbar">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {tab === "programs" && (
              <TextTabs
                testId="dashboard-program-filter"
                value={programFilter}
                onChange={(id) => setTab("programs", id === "active" ? null : id)}
                options={[
                  { id: "active", label: "Active", count: running.length },
                  { id: "past", label: "Past", count: past.length },
                  { id: "all", label: "All", count: programs.length },
                ]}
              />
            )}
            {tab === "instructors" && (
              <TextTabs
                testId="dashboard-instructor-filter"
                value={instructorFilter}
                onChange={(id) => setTab("instructors", id === "all" ? null : id)}
                options={[
                  { id: "all", label: "All", count: instructors.length },
                  { id: "idle", label: "No sessions in 30 days", count: idle.length },
                ]}
              />
            )}
          </div>
        </div>
      )}

      {/* ---- Overview ------------------------------------------------- */}
      {/* At desktop width the chart and Today share a row and a height: Today
          is drawn inside the chart's row (absolutely, so its own length never
          grows it) and a busy day scrolls inside the card. Under them, where
          teaching happens, delivery and text reminders share a row and its
          height; Needs attention runs full width at the bottom, its list
          scrolling past a fixed height. Narrower, Today comes first, then the
          rest in that order. */}
      {tab === "overview" && (
        <div className="flex flex-col gap-6">
          <div className="grid gap-6 xl:grid-cols-3">
            <div className="min-w-0 xl:col-span-2">
              <TeachingVolumeChart
                buckets={series.buckets}
                unit={series.unit}
                rangeLabel={rangeLabel}
                totalSub={
                  period && versus ? (
                    <Delta current={period.sessions} previous={period.previous?.sessions} suffix={versus} testId="volume-total-delta" />
                  ) : undefined
                }
              />
            </div>
            <div className="order-first min-w-0 xl:relative xl:order-none">
              <TodayCard
                date={asOf}
                data={today}
                onRetry={() => setTodayTry((n) => n + 1)}
                onOpen={(path) => navigate(path)}
                className="max-h-[440px] xl:absolute xl:inset-0 xl:max-h-none"
              />
            </div>
          </div>
          <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3" data-testid="dashboard-overview-row">
            <ProgramsCard
              programs={programs}
              rangeLabel={rangeLabel}
              onOpen={(p) => navigate(`/org/programs/${p.program_id}`)}
              onSeeAll={() => setTab("programs")}
            />
            {period && <DeliveryCard period={period} teaching={teaching ?? 0} instructors={data.instructors} />}
            {data.sms ? <SmsCard sms={data.sms} /> : null}
          </div>
          <AttentionCard items={attention} onOpen={openAttention} onChaseAll={chaseAll} chasing={chasingAll} />
        </div>
      )}

      {/* ---- Programs ------------------------------------------------- */}
      {tab === "programs" && (
        <>
          <Card padding={0} className="mb-3">
            <ReportTable
              testId="dashboard-programs"
              columns={programCols}
              rows={shownPrograms}
              rowKey={(p) => p.program_id}
              onRowPress={(p) => navigate(`/org/programs/${p.program_id}`)}
              defaultSort={{ id: "held", dir: "desc" }}
              empty={programFilter === "past" ? "No past programs with sessions in the last year." : "No programs running."}
              footer={
                shownPrograms.length > 1
                  ? {
                      title: "Total",
                      ...(schedule.loading
                        ? {}
                        : {
                            held: formatCount(scheduleSum("held")),
                            change: (
                              <span className="font-sans text-[13px]">
                                <Delta
                                  current={scheduleSum("held_week")}
                                  previous={scheduleSum("held_prev_week")}
                                  suffix="vs last week"
                                  countFromZero
                                  className="flex-nowrap whitespace-nowrap"
                                />
                              </span>
                            ),
                            remaining: formatCount(scheduleSum("remaining", (row) => !row.private_lessons)),
                          }),
                      notes_owed: formatCount(sum(shownPrograms, "notes_owed")),
                    }
                  : null
              }
            />
          </Card>
          <p className="max-w-3xl font-sans text-[12px] leading-relaxed text-[var(--text-tertiary)]">
            Sessions taught counts every session held since the program began: each class meeting that has taken
            place and was not cancelled, whether or not anyone wrote notes (how many did is under the number), and
            each solo lesson with notes. Change compares the sessions held this week so far, from Monday, with the same
            days last week. Remaining is the sessions still to come to the end of the program: its end date, or
            without one, its classes&rsquo; last scheduled meeting. A class that repeats with no end date leaves no
            rest to count, so the next 30 days stand in. Private lessons are booked in Kalver Scheduler and are not
            counted here. None of these follows the range. Students are everyone on a program&rsquo;s class rosters and scheduled lessons, so one
            student can appear under several programs. Missing notes cover class meetings in the last{" "}
            {NOTES_WINDOW_DAYS} days.
          </p>
        </>
      )}

      {/* ---- Instructors ---------------------------------------------- */}
      {tab === "instructors" && (
        <>
          <Card padding={0} className="mb-3">
            <ReportTable
              testId="dashboard-instructors"
              columns={instructorCols}
              rows={shownInstructors}
              rowKey={(r) => r.user_id}
              onRowPress={(r) => navigate(`/org/instructors/${r.user_id}`)}
              canPress={(r) => r.member}
              defaultSort={{ id: "sessions_30d", dir: "desc" }}
              empty={instructorFilter === "idle" ? "Every instructor taught in the last 30 days." : "No instructors yet."}
            />
          </Card>
          <p className="max-w-3xl font-sans text-[12px] leading-relaxed text-[var(--text-tertiary)]">
            Org-attributed sessions only: what an instructor teaches privately is not counted here. A former
            instructor stays listed while their sessions for your programs are in the last year.
          </p>
        </>
      )}

      {/* ---- Students ------------------------------------------------- */}
      {tab === "students" && <OrgStudentsTab filter={filter} onFilter={(f) => setTab("students", f)} />}

      {/* ---- Lessons -------------------------------------------------- */}
      {tab === "lessons" && (
        <OrgLessonsTab
          key={lessonsVisit}
          range={period ? { label: rangeLabel, start: period.start, end: period.end } : null}
          filter={filter}
          onFilter={(f) => setTab("lessons", f)}
        />
      )}

      <p className="mt-8 font-sans text-[12px] text-[var(--text-tertiary)]">
        Exports are your own rows as CSV. Lesson transcripts and instructors&apos; private notes are never included.
      </p>
    </PageShell>
  );
}
